"use client";

import { useContext, useEffect, useState } from "react";
import { ReactSVG } from "react-svg";

import { blackFrame } from "@/components/QRFrame/frames/black";
import { boardFrame } from "@/components/QRFrame/frames/board";
import { buttonFrame } from "@/components/QRFrame/frames/button";
import { cafeFrame } from "@/components/QRFrame/frames/cafe";
import { envelopeFrame } from "@/components/QRFrame/frames/envelope";
import { mobileFrame } from "@/components/QRFrame/frames/mobile";
import { museumFrame } from "@/components/QRFrame/frames/museum";
import { receiptFrame } from "@/components/QRFrame/frames/receipt";
import { shoppingFrame } from "@/components/QRFrame/frames/shopping";
import { ticketFrame } from "@/components/QRFrame/frames/ticket";
import { whiteFrame } from "@/components/QRFrame/frames/white";

import { QREditorContext, INITIAL_QR_DATA } from "@/contexts/QREditorContext";

import { useQRStyle } from "@/hooks/useQR";

import { isSafari, isClient } from "@/utils/browser";
import { getQRCodeUrl } from "@/utils/qrEditorHelper";

export const QRPreviewStyle = ({ data }: { data?: any }) => {
  const qrEditorContext = useContext(QREditorContext);

  const { ref, updateQRCode, url, setUrl, qrCode } = useQRStyle();
  const frame = qrEditorContext.qrData.qrStyle;

  const [urlQr, setUrlQr] = useState(undefined);

  const getFrameWrapperStyle = () => {
    return `qr-preview__canvas__wrapper ${
      frame?.frameStyle === "none" ||
      frame?.frameStyle === undefined ||
      qrEditorContext.qrData?.qrStyle?.styleType === "AI"
        ? "qr-preview__canvas__wrapper--none"
        : ""
    }`;
  };

  const getQrCode = () => {
    const qrStyleType = qrEditorContext.qrData?.qrStyle?.styleType;
    const selectedQrCode = qrEditorContext.qrData?.ai?.selectedImageUrl;

    return qrStyleType === "AI" ? (
      selectedQrCode ? (
        <img src={selectedQrCode} />
      ) : (
        <img src="/images/empty-logo.png" />
      )
    ) : (
      <div ref={ref}></div>
    );
  };

  const getFrame = () => {
    const initialFrameValues = INITIAL_QR_DATA.qrStyle;
    const qrStyleType = qrEditorContext.qrData?.qrStyle?.styleType;

    const sharedProps = {
      children: <div ref={ref}></div>,
      frameBackgroundColor:
        frame?.frameBackgroundColor ?? initialFrameValues.frameBackgroundColor,
      frameColor: frame?.frameColor ?? initialFrameValues.frameColor,
      text: frame?.frameText ?? initialFrameValues.frameText,
      urlQr: isSafari() ? data?.image : urlQr,
      textColor: frame?.frameTextColor ?? initialFrameValues.frameTextColor,
    };

    if (qrStyleType === "AI") return getQrCode();

    switch (frame?.frameStyle) {
      case undefined:
      case "none":
        return getQrCode();
      case "ticket":
        return ticketFrame(sharedProps);
      case "mobile":
        return mobileFrame(sharedProps);
      case "black":
        return blackFrame(sharedProps);
      case "white":
        return whiteFrame(sharedProps);
      case "button":
        return buttonFrame(sharedProps);
      case "museum":
        return museumFrame(sharedProps);
      case "cafe":
        return cafeFrame(sharedProps);
      case "shopping":
        return shoppingFrame(sharedProps);
      case "board":
        return boardFrame(sharedProps);
      case "envelope":
        return envelopeFrame(sharedProps);
      case "receipt":
        return receiptFrame(sharedProps);
    }
  };

  useEffect(() => {
    if (isClient() && isSafari() && qrCode) {
      setTimeout(() => {
        qrCode
          ?.getRawData("png")
          .then((data) => data && setUrlQr(URL.createObjectURL(data)));
      }, 100);
    }
  }, [qrCode]);

  useEffect(() => {
    updateQRCode({
      qrUrl: url,
      qrStyle: data?.qrStyle,
    });
  }, [data, url]);

  useEffect(() => {
    if (qrEditorContext?.qrData.qrCodeReference) {
      const qrUrl = getQRCodeUrl(qrEditorContext.qrData, true);
      if (qrUrl) setUrl(qrUrl);
    }
  }, [qrEditorContext?.qrData.qrCodeReference]);

  return (
    <div className="qr-preview__layout__image">
      <div className="qr-preview__canvas" id="canvas">
        <div className="frame">
          <div className={getFrameWrapperStyle()}>{getFrame() as any}</div>
        </div>
      </div>

      <ReactSVG
        src={`/images/phone-previews/phone-empty.svg`}
        className="preview-frame"
      />
    </div>
  );
};
